import { SdsContextMenuSelectItem } from '../context-menu/item/sds-cm-select-item.lit';
import { SdsContextMenuItem } from '../context-menu/item/sds-cm-item.lit';
import { SdsContextMenu } from '../context-menu/sds-context-menu.lit';
import { SdsContextMenuGroup } from '../context-menu/group/sds-cm-group.lit';
function findNextItem(items, startIndex, direction) {
    let nextIndex = startIndex + direction;
    // Handle wrapping around
    if (nextIndex >= items.length) {
        nextIndex = 0; // Wrap to first item
    }
    else if (nextIndex < 0) {
        nextIndex = items.length - 1; // Wrap to last item
    }
    return nextIndex;
}
function focusEnabledItem(menu, direction, isSelectItem = true) {
    const items = isSelectItem
        ? getMenuItems(menu, true)
        : getMenuItems(menu, false);
    const currentIndex = items.findIndex(item => item.focused);
    let nextIndex;
    if (currentIndex === -1) {
        // No item is currently focused, focus first item on down or last item on up
        nextIndex = direction === 1 ? 0 : items.length - 1;
    }
    else {
        nextIndex = findNextItem(items, currentIndex, direction);
    }
    if (nextIndex >= 0 && nextIndex < items.length && nextIndex !== currentIndex) {
        // Clear focus from current item if it exists
        if (currentIndex !== -1) {
            items[currentIndex].focused = false;
        }
        // Set focus on next item (even if disabled)
        items[nextIndex].focused = true;
        return items[nextIndex];
    }
    // If no change, return the current focused item or undefined
    return currentIndex !== -1 ? items[currentIndex] : undefined;
}
// Generic functions for focusing items in any direction
export function focusNextItem(menu, isSelectItem = true) {
    return focusEnabledItem(menu, 1, isSelectItem);
}
export function focusPreviousItem(menu, isSelectItem = true) {
    return focusEnabledItem(menu, -1, isSelectItem);
}
export function focusItem(menu, condition, offset = 0) {
    const selectItems = getMenuItems(menu, true);
    for (let i = 0; i < selectItems.length; i++) {
        const item = selectItems[i];
        if (item.focused) {
            if (condition(selectItems, i)) {
                item.focused = false;
                const newIndex = i + offset;
                const newItem = selectItems[newIndex];
                newItem.focused = true;
                return newItem.identifier;
            }
            return item.identifier;
        }
    }
    if (selectItems[0]) {
        const firstItem = selectItems[0];
        firstItem.focused = true;
        return firstItem.identifier;
    }
}
export function initMenuItemsFocus(menu) {
    if (menu) {
        return focusSelectedOrFirstItem(getMenuItems(menu, true));
    }
}
export function initMenuItemsFocusForButtonDropdown(menu) {
    if (menu) {
        const menuItems = getMenuItems(menu, false);
        return focusFirstEnabledMenuItem(menuItems);
    }
}
export function cleanMenuItemsFocus(menu, isSelectItem = true) {
    if (menu) {
        if (isSelectItem) {
            getMenuItems(menu, true).forEach(item => {
                item.focused = false;
            });
        }
        else {
            getMenuItems(menu, false).forEach(item => {
                item.focused = false;
            });
        }
    }
}
export function focusSelectedOrFirstItem(items) {
    // Clear all focus first
    items.forEach(item => (item.focused = false));
    // Priority order: selected items > select all item > first item
    const selectedItem = items.find(item => item.selected);
    if (selectedItem) {
        selectedItem.focused = true;
        return selectedItem.identifier;
    }
    const selectAllItem = items.find(item => item.id === 'select-all-item');
    if (selectAllItem) {
        selectAllItem.focused = true;
        return selectAllItem.identifier;
    }
    const firstItem = items[0];
    if (firstItem) {
        firstItem.focused = true;
        return firstItem.identifier;
    }
}
export function focusFirstEnabledMenuItem(items) {
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.disabled) {
            item.focused = true;
            return item;
        }
    }
    return undefined;
}
export function getFocusedItem(menu, isSelectItem = true) {
    if (isSelectItem) {
        const items = getMenuItems(menu, true);
        return items.find(item => item.focused);
    }
    else {
        const items = getMenuItems(menu, false);
        return items.find(item => item.focused);
    }
}
export function scrollFocusedItemIfNeeded(menu, isSelectItem = true) {
    const item = getFocusedItem(menu, isSelectItem);
    if (item) {
        scrollItemIfNeeded(menu, item);
    }
}
export function scrollToSelected(menu, isSelectItem = true) {
    var _a;
    const focusedItem = getFocusedItem(menu, isSelectItem);
    if (focusedItem) {
        const itemRect = focusedItem.getBoundingClientRect();
        const menuDiv = menu.getMenu() instanceof SdsContextMenu
            ? (_a = menu.getMenu().shadowRoot) === null || _a === void 0 ? void 0 : _a.querySelector('div')
            : menu.getMenu();
        if (menuDiv) {
            const menuRect = menuDiv.getBoundingClientRect();
            const scrollOffset = itemRect.top - menuRect.top - 120;
            menuDiv.scrollBy(0, scrollOffset);
        }
    }
}
function scrollItemIfNeeded(menu, item) {
    const itemRect = item.getBoundingClientRect();
    const menuRect = menu.getMenu().getBoundingClientRect();
    if (itemRect.bottom > menuRect.bottom) {
        menu.getMenu().scrollBy(0, itemRect.bottom - menuRect.bottom);
    }
    if (itemRect.top < menuRect.top) {
        menu.getMenu().scrollBy(0, itemRect.top - menuRect.top);
    }
}
export function getMenuItems(contextMenu, isSelectItem = true) {
    var _a;
    const items = [];
    const menuItems = ((_a = contextMenu.querySelector('slot')) === null || _a === void 0 ? void 0 : _a.assignedElements()) || [];
    const ItemType = isSelectItem ? SdsContextMenuSelectItem : SdsContextMenuItem;
    // Add select-all item if needed (only for select items)
    if (isSelectItem) {
        const selectAllItem = contextMenu.querySelector('sds-cm-select-item[id="select-all-item"]');
        if (selectAllItem) {
            menuItems.unshift(selectAllItem);
        }
    }
    for (let i = 0; i < menuItems.length; i++) {
        const menuItem = menuItems[i];
        if (menuItem instanceof ItemType && menuItem.style.display !== 'none') {
            items.push(menuItem);
        }
        if (menuItem instanceof SdsContextMenuGroup) {
            if (menuItem.style.display !== 'none') {
                menuItem.filteredContent.forEach(groupItem => {
                    if (groupItem instanceof ItemType && groupItem.style.display !== 'none') {
                        items.push(groupItem);
                    }
                });
            }
        }
    }
    return items;
}
